import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import Modal from '../components/Modal';
import Button from '../components/Button';
import SearchableSelect from '../components/SearchableSelect';
import MonthFilter from '../components/MonthFilter';
import TableScrollContainer from '../components/TableScrollContainer';
import { buildMonthOptions, matchesSelectedMonths } from '../utils/monthFilters';
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../utils/currency';
import './Despesa.css';

const initialFormData = () => ({
  categoria_id: '',
  descricao: '',
  valor: '',
  observacoes: '',
  data_competencia: new Date().toISOString().slice(0, 10),
  data_pagamento: new Date().toISOString().slice(0, 10),
});

function Despesa() {
  const [lancamentos, setLancamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData());
  const [loading, setLoading] = useState(true);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [monthDateValues, setMonthDateValues] = useState([]);

  useEffect(() => {
    fetchData(selectedMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonths]);

  const buildMonthQuery = (months = []) => {
    if (!months || months.length === 0) return '';
    return `?meses=${encodeURIComponent(months.join(','))}`;
  };

  const fetchData = async (months = []) => {
    try {
      const [despResponse, catResponse] = await Promise.all([
        axios.get(`/api/lancamentos-despesa${buildMonthQuery(months)}`),
        axios.get('/api/categorias-despesa')
      ]);

      setLancamentos(despResponse.data);
      if (!months || months.length === 0) {
        setMonthDateValues(despResponse.data.map((item) => item.data_competencia || item.data_lancamento));
      }
      setCategorias(catResponse.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valorNormalizado = parseCurrencyInput(formData.valor);
    if (!valorNormalizado) {
      alert('Informe um valor válido.');
      return;
    }

    const payload = {
      ...formData,
      valor: valorNormalizado,
    };

    try {
      await axios.post('/api/lancamentos-despesa', payload);
      await fetchData();
      setShowModal(false);
      setFormData(initialFormData());
    } catch (error) {
      console.error('Erro ao salvar despesa:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar a despesa.');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post('/api/categorias-despesa', { nome: newCategory });
      await fetchData();
      setNewCategory('');
      setShowNewCategory(false);
    } catch (error) {
      console.error('Erro ao adicionar categoria:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza?')) return;

    try {
      await axios.delete(`/api/lancamentos-despesa/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Erro ao deletar despesa:', error);
    }
  };

  const monthOptions = useMemo(
    () => buildMonthOptions(monthDateValues),
    [monthDateValues]
  );
  const lancamentosFiltrados = useMemo(
    () => lancamentos.filter((item) => matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths)),
    [lancamentos, selectedMonths]
  );
  const totalDespesa = lancamentosFiltrados.reduce((sum, lanc) => sum + parseFloat(lanc.valor || 0), 0);

  return (
    <div className="despesa">
      <div className="header-section">
        <h1>Despesas</h1>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          <FiPlus /> Nova Despesa
        </Button>
      </div>

      <MonthFilter
        title="Filtrar meses"
        options={monthOptions}
        selectedMonths={selectedMonths}
        onChange={setSelectedMonths}
      />

      <div className="total-section">
        <div className="total-card">
          <h3>Total de Despesas (Período)</h3>
          <p className="total-value">{formatCurrency(totalDespesa)}</p>
        </div>
      </div>

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <div className="card">
          <TableScrollContainer rows={lancamentosFiltrados.length}>
            <table>
            <thead>
              <tr>
                <th>Competência</th>
                <th>Categoria</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Pagamento</th>
                <th>Observações</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lancamentosFiltrados.map((lanc) => (
                <tr key={lanc.id}>
                  <td>{new Date(`${lanc.data_competencia}T00:00:00`).toLocaleDateString()}</td>
                  <td><strong>{lanc.categoria_nome}</strong></td>
                  <td>{lanc.descricao}</td>
                  <td className="valor">{formatCurrency(lanc.valor)}</td>
                  <td>{new Date(`${lanc.data_pagamento}T00:00:00`).toLocaleDateString()}</td>
                  <td>{lanc.observacoes || '-'}</td>
                  <td className="actions">
                    <button className="delete-btn" onClick={() => handleDelete(lanc.id)} title="Deletar">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </TableScrollContainer>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setFormData(initialFormData());
        }}
        title="Registrar Despesa"
      >
        <form onSubmit={handleSubmit} className="form">
          <label>
            Categoria *
            <div className="category-select-group">
              <SearchableSelect
                required
                value={formData.categoria_id}
                onChange={(value) => setFormData({ ...formData, categoria_id: value })}
                placeholder="Selecione uma categoria"
                searchPlaceholder="Digite o nome da categoria"
                options={categorias.map((categoria) => ({
                  value: String(categoria.id),
                  label: categoria.nome,
                  searchText: categoria.nome,
                }))}
              />
              <Button type="button" variant="secondary" size="small" onClick={() => setShowNewCategory(!showNewCategory)}>
                Nova
              </Button>
            </div>
          </label>

          {showNewCategory && (
            <div className="new-category-form">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Nome da categoria"
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button type="button" variant="success" size="small" onClick={handleAddCategory}>Adicionar</Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setShowNewCategory(false);
                    setNewCategory('');
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          <label>
            Descrição *
            <input
              type="text"
              required
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Ex: Compra de esmalte, Aluguel mensal, etc"
            />
          </label>

          <label>
            Valor (R$) *
            <input
              type="text"
              inputMode="decimal"
              required
              value={formData.valor}
              onChange={(e) => setFormData({ ...formData, valor: formatCurrencyInput(e.target.value) })}
              placeholder="0,00"
            />
          </label>

          <div className="date-grid">
            <label>
              Data da despesa / competência
              <input
                type="date"
                value={formData.data_competencia}
                onChange={(e) => setFormData({ ...formData, data_competencia: e.target.value })}
              />
            </label>
            <label>
              Data de pagamento
              <input
                type="date"
                value={formData.data_pagamento}
                onChange={(e) => setFormData({ ...formData, data_pagamento: e.target.value })}
              />
            </label>
          </div>

          <label>
            Observações
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Anotações adicionais"
              rows="3"
            />
          </label>

          <div className="form-actions">
            <Button type="submit" variant="primary">Registrar</Button>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Despesa;
